import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaSearch, FaBars, FaTimes } from "react-icons/fa";
import { apiUrl } from "../../../config/config";
import SearchBar from "./SearchBar";
import CartCount from "./CartCount";
import logo from "../../../assets/shirtspatches.png";
import { FaUser } from "react-icons/fa";
import { UserPlus, Phone, Mail, Truck } from "lucide-react";
import { brands } from "../../../config/config";
import { useBrand } from "../../../context/BrandContext";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [closingDrawer, setClosingDrawer] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [closingMobileSearch, setClosingMobileSearch] = useState(false);

  const [showQuoteDrawer, setShowQuoteDrawer] = useState(false);
  const [closingQuoteDrawer, setClosingQuoteDrawer] = useState(false);

  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    patch_type: "",
    backing_type: "",
    quantity: "",
    width: "",
    height: "",
    comments: "",
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const { brandData, loading } = useBrand();
  const dropdownRef = useRef(null);
  const quoteDrawerRef = useRef(null);
  const location = useLocation();

  const headerIconClass =
    "w-9 h-9 rounded-lg border border-[#e7ddff] bg-white text-[#6f4bd8] flex items-center justify-center shadow-sm hover:bg-[#f5f0ff] hover:text-[#211b33] transition";

  const desktopButtonClass =
    "flex h-9 min-w-[112px] items-center justify-center rounded-lg px-3 text-sm font-semibold shadow-sm transition";

  const inputStyle =
    "w-full rounded-2xl border border-pastelPurple bg-white px-4 py-3 text-sm font-medium text-darkPurple outline-none transition placeholder:text-[#8a829a] focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60";

  const openDrawer = () => {
    setClosingDrawer(false);
    setOpen(true);
  };

  const closeDrawer = () => {
    setClosingDrawer(true);
    setTimeout(() => {
      setOpen(false);
      setClosingDrawer(false);
    }, 320);
  };

  const openQuoteDrawer = () => {
    setErrors({});
    setClosingQuoteDrawer(false);
    setShowQuoteDrawer(true);
  };

  const closeQuoteDrawer = () => {
    setClosingQuoteDrawer(true);
    setTimeout(() => {
      setShowQuoteDrawer(false);
      setClosingQuoteDrawer(false);
    }, 320);
  };

  const toggleMobileSearch = () => {
    if (showMobileSearch) {
      setClosingMobileSearch(true);
      setTimeout(() => {
        setShowMobileSearch(false);
        setClosingMobileSearch(false);
      }, 280);
    } else {
      setClosingMobileSearch(false);
      setShowMobileSearch(true);
    }
  };

  const getErrorText = (error) => {
    if (!error) return "";
    if (Array.isArray(error)) return error[0];
    return error;
  };

  const handleQuoteChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const validateQuoteForm = () => {
    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name required";

    if (!form.email) {
      newErrors.email = "Email required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Invalid email";
    }

    if (!form.phone) newErrors.phone = "Phone required";
    if (!form.quantity) newErrors.quantity = "Quantity required";
    if (!form.width) newErrors.width = "Width required";
    if (!form.height) newErrors.height = "Height required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const submitQuoteForm = async () => {
    if (!validateQuoteForm()) return;

    const formData = new FormData();

    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("patch_type", form.patch_type);
    formData.append("backing_type", form.backing_type);
    formData.append("quantity", parseInt(form.quantity));
    formData.append("width", parseFloat(form.width));
    formData.append("height", parseFloat(form.height));
    formData.append("comments", form.comments);
    formData.append("brand", brands);

    if (file) formData.append("image", file);

    try {
      const response = await fetch(`${apiUrl}/api/instant-quote`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await response.json();

      if (data.errors) {
        setErrors(data.errors);
        return;
      }

      if (data.status) {
        alert("Quote Submitted!");

        setForm({
          name: "",
          email: "",
          phone: "",
          patch_type: "",
          backing_type: "",
          quantity: "",
          width: "",
          height: "",
          comments: "",
        });

        setFile(null);
        closeQuoteDrawer();
      }
    } catch (err) {
      alert("Server Error");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const handleQuoteOutside = (event) => {
      if (
        quoteDrawerRef.current &&
        !quoteDrawerRef.current.contains(event.target)
      ) {
        closeQuoteDrawer();
      }
    };

    if (showQuoteDrawer && !closingQuoteDrawer) {
      document.addEventListener("mousedown", handleQuoteOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleQuoteOutside);
    };
  }, [showQuoteDrawer, closingQuoteDrawer]);

  useEffect(() => {
    if (showQuoteDrawer) {
      setShowQuoteDrawer(false);
      setClosingQuoteDrawer(false);
    }
  }, [location]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.id) {
      fetch(`${apiUrl}/api/users/${storedUser.id}`)
        .then((res) => res.json())
        .then((data) => setUser(data))
        .catch(() => {});
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    window.location.href = "/login";
  };

  useEffect(() => {
    fetch(`${apiUrl}/api/${brands}/category`)
      .then((res) => res.json())
      .then((data) => setCategories(data.data))
      .catch((err) => console.log("Category fetch error:", err));
  }, []);

  useEffect(() => {
    fetch(`${apiUrl}/api/${brands}/products`)
      .then((res) => res.json())
      .then((data) => setProducts(data.data))
      .catch((err) => console.log("Products fetch error:", err));
  }, []);

  return (
    <>
      <style>
        {`
          @keyframes drawerSlideIn {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(0); }
          }

          @keyframes drawerSlideOut {
            0% { transform: translateX(0); }
            100% { transform: translateX(-100%); }
          }

          @keyframes drawerFadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          @keyframes drawerFadeOut {
            0% { opacity: 1; }
            100% { opacity: 0; }
          }

          @keyframes mobileSearchIn {
            0% {
              opacity: 0;
              transform: translateY(-10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes mobileSearchOut {
            0% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-10px);
            }
          }

          @keyframes quoteOverlayIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          @keyframes quoteOverlayOut {
            0% { opacity: 1; }
            100% { opacity: 0; }
          }

          @keyframes quoteDrawerIn {
            0% {
              opacity: 0;
              transform: translateX(100%);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes quoteDrawerOut {
            0% {
              opacity: 1;
              transform: translateX(0);
            }
            100% {
              opacity: 0;
              transform: translateX(100%);
            }
          }
        `}
      </style>

      {/* TOP HEADER */}
      <div className="hidden w-full border-b border-[#eadfff] bg-[#fbf9ff] md:block">
        <div className="container mx-auto flex items-center justify-between px-4 py-1.5 text-[11px] text-[#211b33]">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 font-medium">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e7ddff] bg-[#f1ebff]">
                <Phone size={13} className="text-[#6f4bd8]" />
              </span>
              Call Us: (703)-686-8193
            </span>

            <span className="flex items-center gap-2 font-medium">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e7ddff] bg-[#f1ebff]">
                <Mail size={13} className="text-[#6f4bd8]" />
              </span>
              support@shirtspatches.com
            </span>
          </div>

          <span className="flex items-center gap-2 rounded-lg border border-[#c7ed3f] bg-[#d7ff4f] px-3 py-1 text-[11px] font-bold text-[#211b33]">
            <Truck size={14} />
            FREE SHIPPING ON ALL ORDERS OVER $200
          </span>
        </div>
      </div>

      {/* MAIN HEADER */}
      <div className="container mx-auto flex items-center justify-between px-4 py-1.5 md:py-2">
        <Link to="/" className="hidden lg:block">
          <img src={logo} alt="Logo" className="w-24" />
        </Link>

        <Link to="/" className="lg:hidden">
          <img src={logo} alt="Logo" className="w-20" />
        </Link>

        <div className="hidden flex-1 justify-center px-8 lg:flex">
          <SearchBar />
        </div>

        <div className="hidden items-center gap-2.5 text-textGray lg:flex">
          <button
            type="button"
            onClick={openQuoteDrawer}
            className={`${desktopButtonClass} bg-[#d7ff4f] text-[#211b33] hover:-translate-y-0.5 hover:bg-[#c8f531]`}
          >
            Get a Quote
          </button>
          
          <Link
            to="/cart"
            className={`${desktopButtonClass} relative gap-2 border border-[#e7ddff] bg-white text-[#211b33] hover:bg-[#f5f0ff]`}
          >
            <span>Cart</span>
            <CartCount />
          </Link>

          <div className="relative" ref={dropdownRef}>
            {user ? (
              <>
                <button
                  type="button"
                  className={`${desktopButtonClass} gap-2 border border-[#e7ddff] bg-white text-[#211b33] hover:bg-[#f5f0ff]`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span>{user.name}</span>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-40 rounded-lg border bg-white shadow-lg">
                    <p className="px-4 py-2 font-semibold text-gray-700">
                      {user.name}
                    </p>

                    <Link
                      to="/account"
                      className="block px-4 py-2 hover:bg-gray-200"
                      onClick={() => setDropdownOpen(false)}
                    >
                      My Account
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Link
                to="/login"
                className={`${desktopButtonClass} gap-2 border border-[#e7ddff] bg-white text-[#211b33] hover:bg-[#f5f0ff]`}
              >
                <span>Sign In</span>
                <FaUser className="text-xs text-[#6f4bd8]" />
              </Link>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5 text-textGray lg:hidden">
          <button
            type="button"
            onClick={openQuoteDrawer}
            className="flex h-9 items-center justify-center rounded-lg bg-[#d7ff4f] px-3 text-xs font-extrabold text-[#211b33] shadow-sm transition hover:bg-[#c8f531]"
          >
            Quote
          </button>

          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={headerIconClass}
          >
            <UserPlus className="h-4 w-4" />
          </button>

          {dropdownOpen && (
            <div className="absolute right-4 top-12 z-50 w-40 rounded-lg border bg-white shadow-lg">
              {!user ? (
                <Link to="/login" className="block px-4 py-2 hover:bg-gray-200">
                  Sign In
                </Link>
              ) : (
                <>
                  <p className="px-4 py-2 font-semibold text-gray-700">
                    {user.name}
                  </p>

                  <Link
                    to="/account"
                    className="block px-4 py-2 hover:bg-gray-200"
                  >
                    My Account
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-100"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          )}

          <button
            type="button"
            className={headerIconClass}
            onClick={toggleMobileSearch}
          >
            <FaSearch className="text-sm" />
          </button>

          <button type="button" className={headerIconClass} onClick={openDrawer}>
            <FaBars className="text-base" />
          </button>
        </div>
      </div>

      {showMobileSearch && (
        <div
          className="bg-mainBg px-4 pb-2 lg:hidden"
          style={{
            animation: closingMobileSearch
              ? "mobileSearchOut 0.28s ease both"
              : "mobileSearchIn 0.3s ease both",
          }}
        >
          <SearchBar mobile />
        </div>
      )}

      {/* CATEGORY NAVIGATION */}
      <div
        className="sticky top-0 z-40 hidden w-full border-y border-[#34284f] bg-[#211b33] text-sm text-white shadow-[0_8px_24px_rgba(33,27,51,0.18)] lg:block"
        onMouseLeave={() => setActiveMenu(null)}
      >
        <ul className="flex items-center justify-center gap-2 whitespace-nowrap px-4">
          {categories.map((cat) => {
            const menuProducts = products
              .filter((p) => p.category_id === cat.id)
              .slice(0, 8);

            const columns = Math.min(menuProducts.length, 4);

            return (
              <li
                key={cat.id}
                className="relative cursor-pointer py-1.5"
                onMouseEnter={() => setActiveMenu(cat.id)}
              >
                <Link
                  to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}`}
                  className="group relative inline-flex items-center justify-center px-4 py-1.5 text-[13px] font-semibold text-[#f8f4ff] transition-all duration-300 hover:text-[#d7ff4f]"
                >
                  <span className="relative">
                    {cat.category_name}
                    <span className="absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 rounded-full bg-[#d7ff4f] transition-all duration-300 group-hover:w-full"></span>
                  </span>
                </Link>

                {activeMenu === cat.id && menuProducts.length > 0 && (
                  <div
                    className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2"
                    onMouseEnter={() => setActiveMenu(cat.id)}
                    onMouseLeave={() => setActiveMenu(null)}
                  >
                    <div className="inline-block max-w-[calc(100vw-32px)] overflow-x-auto rounded-xl border border-[#eadfff] bg-white p-4 shadow-[0_18px_45px_rgba(18,12,36,0.24)]">
                      <div
                        className="grid items-stretch justify-center gap-4"
                        style={{
                          gridTemplateColumns: `repeat(${columns}, minmax(190px, 210px))`,
                        }}
                      >
                        {menuProducts.map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}/${product.name.replace(/\s+/g, "-")}/${product.id}`}
                            className="group flex h-[255px] flex-col rounded-xl border border-[#eadfff] bg-white p-3 shadow-[0_10px_25px_rgba(47,34,84,0.07)] transition-all duration-300 hover:-translate-y-1 hover:border-[#d8c8ff] hover:shadow-[0_18px_40px_rgba(47,34,84,0.13)]"
                          >
                            <div className="flex h-32 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#f2ecff] to-[#f8ffe0] p-3">
                              <img
                                src={`${apiUrl}/storage/${product.image}`}
                                alt={product.name}
                                className="max-h-full max-w-full rounded object-contain transition duration-300 group-hover:scale-105"
                              />
                            </div>

                            <div className="flex flex-1 flex-col px-1 pb-1 pt-3 text-center">
                              <h3 className="min-h-[42px] whitespace-normal break-words text-[13px] font-bold leading-[17px] text-[#211b33]">
                                {product.name}
                              </h3>

                              <p className="mt-2 text-[12px] font-bold text-[#6f4bd8]">
                                As low as{" "}
                                <span className="font-extrabold text-[#5f7f00]">
                                  ${product.discountedPrice}
                                </span>
                              </p>

                              <div className="mx-auto mt-auto inline-flex rounded-lg bg-[#d7ff4f] px-4 py-1.5 text-[11px] font-extrabold text-[#211b33] transition-all duration-300 group-hover:bg-[#c8f531]">
                                View Product
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* MOBILE DRAWER */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 lg:hidden"
          style={{
            animation: closingDrawer
              ? "drawerFadeOut 0.32s ease both"
              : "drawerFadeIn 0.32s ease both",
          }}
        >
          <div
            className="fixed left-0 top-0 h-full w-72 overflow-y-auto bg-mainBg p-5 shadow-lg"
            style={{
              animation: closingDrawer
                ? "drawerSlideOut 0.32s ease both"
                : "drawerSlideIn 0.32s ease both",
            }}
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-textGray">Menu</h2>
              <FaTimes
                className="cursor-pointer text-2xl"
                onClick={closeDrawer}
              />
            </div>

            <nav className="space-y-1 px-3 py-4">
              <button
                type="button"
                onClick={() => {
                  closeDrawer();
                  setTimeout(() => {
                    openQuoteDrawer();
                  }, 330);
                }}
                className="mb-3 flex w-full items-center justify-center rounded-md bg-[#d7ff4f] px-3 py-3 text-sm font-semibold text-[#211b33] transition hover:bg-[#c8f531] active:bg-[#c8f531]"
              >
                Get a Quote
              </button>

              <Link
                to="/"
                onClick={closeDrawer}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition hover:bg-gray-100 active:bg-gray-200"
              >
                Home
              </Link>

              <Link
                to="/products"
                onClick={closeDrawer}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition hover:bg-gray-100 active:bg-gray-200"
              >
                Shop
              </Link>

              <Link
                to="/about-us"
                onClick={closeDrawer}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition hover:bg-gray-100 active:bg-gray-200"
              >
                About
              </Link>

              <Link
                to="/contact-us"
                onClick={closeDrawer}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition hover:bg-gray-100 active:bg-gray-200"
              >
                Contact
              </Link>

              <div className="pt-2">
                {!user ? (
                  <Link
                    to="/login"
                    onClick={closeDrawer}
                    className="flex items-center justify-center rounded-md bg-[#d7ff4f] px-3 py-3 text-sm font-semibold text-[#211b33] transition hover:bg-[#c8f531] active:bg-[#c8f531]"
                  >
                    Sign In
                  </Link>
                ) : (
                  <Link
                    to="/account"
                    onClick={closeDrawer}
                    className="flex items-center justify-center rounded-md bg-[#d7ff4f] px-3 py-3 text-sm font-semibold text-[#211b33] transition hover:bg-[#c8f531] active:bg-[#c8f531]"
                  >
                    My Account
                  </Link>
                )}
              </div>
            </nav>

            <div className="space-y-3">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="overflow-hidden rounded-xl border border-[#eadfff] bg-white"
                >
                  <div
                    className="flex cursor-pointer items-center justify-between bg-[#fbf9ff] px-4 py-3 transition active:bg-[#f5f0ff]"
                    onClick={() =>
                      setMobileOpenMenu(
                        mobileOpenMenu === cat.id ? null : cat.id
                      )
                    }
                  >
                    <span className="text-sm font-semibold text-[#211b33]">
                      {cat.category_name}
                    </span>

                    <span className="text-xl font-bold text-[#6f4bd8]">
                      {mobileOpenMenu === cat.id ? "−" : "+"}
                    </span>
                  </div>

                  {mobileOpenMenu === cat.id && (
                    <div className="space-y-2 bg-white px-3 py-3">
                      {products
                        .filter((p) => p.category_id === cat.id)
                        .slice(0, 8)
                        .map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}/${product.name.replace(/\s+/g, "-")}/${product.id}`}
                            className="flex items-center gap-3 rounded-lg border border-[#eadfff] p-2 transition hover:bg-[#fbf9ff] active:bg-[#f5f0ff]"
                            onClick={closeDrawer}
                          >
                            <img
                              src={`${apiUrl}/storage/${product.image}`}
                              className="h-16 w-16 flex-shrink-0 rounded-md bg-[#f2ecff] object-contain p-1"
                              alt={product.name}
                            />

                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold text-[#211b33]">
                                {product.name}
                              </p>
                              <p className="mt-1 text-xs font-semibold text-[#6f4bd8]">
                                ${product.discountedPrice}
                              </p>
                            </div>
                          </Link>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GET QUOTE DRAWER */}
      {showQuoteDrawer && (
        <div
          className="fixed inset-0 z-[9999] bg-black/45 backdrop-blur-sm"
          style={{
            animation: closingQuoteDrawer
              ? "quoteOverlayOut 0.32s ease both"
              : "quoteOverlayIn 0.32s ease both",
          }}
        >
          <div
            ref={quoteDrawerRef}
            className="fixed right-0 top-0 h-full w-full max-w-[430px] overflow-hidden bg-white shadow-[0_24px_70px_rgba(18,12,36,0.28)]"
            style={{
              animation: closingQuoteDrawer
                ? "quoteDrawerOut 0.32s ease both"
                : "quoteDrawerIn 0.36s cubic-bezier(0.16,1,0.3,1) both",
            }}
          >
            <div className="relative overflow-hidden bg-darkPurple px-5 py-4 text-white">
              <div className="absolute -left-20 -top-20 h-56 w-56 rounded-full bg-pastelPurple/30 blur-3xl"></div>
              <div className="absolute -right-20 -bottom-20 h-56 w-56 rounded-full bg-keyLime/25 blur-3xl"></div>

              <div className="relative flex w-full items-center justify-between gap-4">
                <div className="min-w-0 flex-1 pr-2">
                  <h3 className="whitespace-normal break-words text-lg font-extrabold leading-tight text-white">
                    Get Instant Quote
                  </h3>
                  <p className="mt-1 text-xs font-medium leading-5 text-[#f2ecff]">
                    Fill details and submit your quote
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeQuoteDrawer}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-base font-extrabold text-white transition hover:bg-keyLime hover:text-darkPurple"
                  aria-label="Close instant quote form"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="h-[calc(100vh-88px)] space-y-3 overflow-y-auto bg-[#fbf9ff] p-4">
              <InputField
                name="name"
                placeholder="Name"
                value={form.name}
                onChange={handleQuoteChange}
                error={getErrorText(errors.name)}
                inputStyle={inputStyle}
              />

              <InputField
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleQuoteChange}
                error={getErrorText(errors.email)}
                inputStyle={inputStyle}
              />

              <InputField
                name="phone"
                type="number"
                placeholder="Phone"
                value={form.phone}
                onChange={handleQuoteChange}
                error={getErrorText(errors.phone)}
                inputStyle={inputStyle}
              />

              <select
                name="patch_type"
                value={form.patch_type}
                onChange={handleQuoteChange}
                className={inputStyle}
              >
                <option value="">Select Patch Type</option>
                <option value="Standard Embroidery">Standard Embroidery</option>
                <option value="3D Embroidery">3D Embroidery</option>
                <option value="PVC Rubber">PVC Rubber</option>
                <option value="Leather">Leather</option>
                <option value="Chenille">Chenille</option>
                <option value="Woven">Woven</option>
                <option value="Printed">Printed</option>
                <option value="Bullion">Bullion</option>
                <option value="Key Chain">Key Chain</option>
              </select>

              <select
                name="backing_type"
                value={form.backing_type}
                onChange={handleQuoteChange}
                className={inputStyle}
              >
                <option value="">Select Backing Type</option>
                <option value="Iron-On">Iron-On</option>
                <option value="Velcro">Velcro</option>
                <option value="Plastic">Plastic</option>
                <option value="Tape/Adhesive">Tape/Adhesive</option>
                <option value="Sew-On">Sew-On</option>
              </select>

              <InputField
                type="number"
                name="quantity"
                placeholder="Quantity"
                value={form.quantity}
                onChange={handleQuoteChange}
                error={getErrorText(errors.quantity)}
                inputStyle={inputStyle}
              />

              <div className="grid grid-cols-2 gap-2">
                <InputField
                  type="number"
                  name="width"
                  placeholder="Width"
                  value={form.width}
                  onChange={handleQuoteChange}
                  error={getErrorText(errors.width)}
                  inputStyle={inputStyle}
                />

                <InputField
                  type="number"
                  name="height"
                  placeholder="Height"
                  value={form.height}
                  onChange={handleQuoteChange}
                  error={getErrorText(errors.height)}
                  inputStyle={inputStyle}
                />
              </div>

              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                className={inputStyle}
              />

              <textarea
                name="comments"
                placeholder="Comments"
                value={form.comments}
                onChange={handleQuoteChange}
                className={`${inputStyle} h-24 resize-none`}
              />

              <button
                type="button"
                onClick={submitQuoteForm}
                className="w-full rounded-2xl bg-keyLime px-6 py-3 text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
              >
                Submit Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const InputField = ({
  type = "text",
  name,
  placeholder,
  value,
  onChange,
  error,
  inputStyle,
}) => (
  <div className="w-full">
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`${inputStyle} ${error ? "border-red-500" : ""}`}
    />

    {error && <p className="mt-1 text-xs font-bold text-red-500">{error}</p>}
  </div>
);
export default Header;
